import { Chip } from 'components/ui'
import React, { memo, useMemo, useState } from 'react'
import { WidgetWrapper } from 'components/TasksV2/UI/WidgetWrapper'
import { Avatar, Flex, Spinner, Text, useToast } from '@chakra-ui/core'
import keys from 'constants/keys'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { StatusCircle } from 'components/ui/StatusCircle'
import colors from 'constants/colors'
import { useDispatch, useSelector } from 'react-redux'
import { isRtlSelector } from 'redux/selectors/general'
import { AppDispatch } from 'config/redux'
import { getTask } from 'redux/actions/tasks_v2'
import { RootState } from 'constants/interfaces'
import { useContextCommentsCounter } from 'components/TasksV2/hooks/useContexsCommentsCounter'
import { ConmmentsCount } from 'components/TasksV2/CommentsSection/ConmmentsCount'
import { getAvatarSrc } from 'components/Tasks/utils'
import { getStatusConfigColor } from '../../utils'
import { CHART_COLORS } from 'utils/taskAnalytics'

interface IProps {
  contextId: string
  userDefaultGroup: string
  statusValue: number
  typeCounts: { [type: number]: number } | null
  taskContextId: string
  tid: string
  onOpen: VoidFunction
  recurrenceTs: string | null
  name: string
  subtasksCount: number
}
export const ContextItem: React.FC<IProps> = memo(
  ({
    contextId,
    userDefaultGroup,
    tid,
    statusValue,
    taskContextId,
    recurrenceTs,
    onOpen,
    name,
    subtasksCount,
    typeCounts,
  }) => {
    const dispatch: AppDispatch = useDispatch()
    const toast = useToast()
    const { groups, retailUsersObject } = useSelector((state: RootState) => state.config.config)

    const { color } = getStatusConfigColor(statusValue)
    const avatar = getAvatarSrc({ id: contextId, groups: groups, retailUsersObject: retailUsersObject! })

    // Calculate completed subtasks count
    const completedCount = useMemo(() => {
      if (!typeCounts) return 0
      return Object.values(typeCounts).reduce((sum, count) => sum + count, 0)
    }, [typeCounts])

    // Get badge color and text color based on status
    const badgeStyle = useMemo(() => {
      switch (statusValue) {
        case keys.TASK_STATUSES.TODO.value:
          return { bg: CHART_COLORS.todo, color: CHART_COLORS.todoText } // серый
        case keys.TASK_STATUSES.INPROGRESS.value:
          return { bg: CHART_COLORS.inProgress, color: CHART_COLORS.inProgressText } // желтый
        case keys.TASK_STATUSES.DONE.value:
        case keys.TASK_STATUSES.SKIPPED.value:
          return { bg: CHART_COLORS.done, color: CHART_COLORS.doneText } // синий
        case keys.TASK_STATUSES.APPROVED.value:
          return { bg: CHART_COLORS.approved, color: CHART_COLORS.approvedText } // зеленый
        case keys.TASK_STATUSES.DISAPPROVED.value:
          return { bg: CHART_COLORS.rejected, color: CHART_COLORS.rejectedText } // красный
        default:
          return { bg: CHART_COLORS.todo, color: CHART_COLORS.todoText } // серый по умолчанию
      }
    }, [statusValue])

    const isRtl = useSelector(isRtlSelector)
    const viewedCommentsIds = useSelector((state: RootState) => state.comments.viewedCommentsIds)
    const {
      deepDiveInsights: { insights },
    } = useSelector((state: RootState) => state.tasks_v2)

    const [isFetching, setFetching] = useState(false)

    const contextIdRecurrenceTs = `${contextId}${recurrenceTs ? '_' + recurrenceTs : ''}`

    const oids = useMemo(() => {
      const context = insights[contextId]

      if (!context || !context.items) {
        return []
      }
      return context.items
        .filter(
          (item) =>
            (item.status === keys.TASK_STATUSES.DONE.value ||
              item.status === keys.TASK_STATUSES.INPROGRESS.value ||
              item.status === keys.TASK_STATUSES.APPROVED.value ||
              item.status === keys.TASK_STATUSES.DISAPPROVED.value) &&
            (item.type === keys.SUBTASK_TYPES.IMAGE_SUBTASK || item.type === keys.SUBTASK_TYPES.VIDEO_SUBTASKS)
        )
        .map((item) => `${item.st_id}_${contextIdRecurrenceTs}`)
    }, [contextId, contextIdRecurrenceTs, insights])

    const { totalCommentsCount, loading } = useContextCommentsCounter(oids)

    const viewedIds = useMemo(
      () =>
        oids.reduce<string[]>((acc, oid) => {
          if (viewedCommentsIds[oid]) {
            acc.push(...viewedCommentsIds[oid])
          }
          return acc
        }, []),
      [oids, viewedCommentsIds]
    )

    const unredCommentsCounter =
      totalCommentsCount - (viewedIds?.length || 0) >= 0 ? totalCommentsCount - (viewedIds?.length || 0) : 0

    const handleContextClick = async () => {
      try {
        setFetching(true)
        const query = {
          tid,
          task_context_id: taskContextId,
          result_context_id: contextId,
          recurrence_ts: recurrenceTs,
        }
        const isResultOk = await dispatch(getTask({ ...query }))
        if (isResultOk) {
          onOpen()
        } else {
          toast({
            status: 'error',
            title: 'Error',
            description: 'Something went wrong',
          })
        }
      } catch (e) {
        console.error(e)
      } finally {
        setFetching(false)
      }
    }

    return (
      <WidgetWrapper
        p="20px"
        h="92px"
        pos="relative"
        cursor="pointer"
        borderRadius="12px"
        border="1px solid rgba(179, 184, 196, 0.4)"
        boxShadow="rgba(173, 173, 173, 0.25) 0px 2px 8px 0px"
        onClick={handleContextClick}
        pointerEvents={isFetching ? 'none' : 'auto'}
      >
        <Flex flexDir="column" w="100%" style={{ rowGap: '10px' }}>
          <Flex alignItems="center" justifyContent="space-between" w="100%">
            <Flex alignItems="center" style={{ columnGap: '25px' }}>
              <StatusCircle color={color} pos="relative" top="unset" right="unset" left="unset" />
              <Avatar size="md" name={name} src={avatar} />
              <Flex flexDir="column">
                <Text
                  fontSize="16px"
                  maxW={{ base: '150px', md: '190px', xl: '100%' }}
                  style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {name}
                </Text>

                {!!userDefaultGroup && (
                  <Text
                    fontSize="14px"
                    color="#9c9c9c"
                    maxW={{ base: '150px', md: '190px', xl: '100%' }}
                    style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {userDefaultGroup}
                  </Text>
                )}
              </Flex>
            </Flex>
            <Flex>
              <Flex alignItems="center" style={{ columnGap: '20px' }} px="20px">
                {/* Progress Badge */}
                <Chip
                  label={
                    <bdi>
                      {completedCount}/{subtasksCount}
                    </bdi>
                  }
                  size="md"
                  radius="12px"
                  bg={badgeStyle.bg}
                  color={badgeStyle.color}
                  border="0"
                  minW="50px"
                  justifyContent="center"
                />

                <ConmmentsCount
                  totalCommentsCount={totalCommentsCount}
                  unredCommentsCounter={unredCommentsCounter}
                  loading={loading}
                  pos="relative"
                  left="unset"
                  right="unset"
                  bottom="unset"
                />
              </Flex>
              {isFetching ? (
                <Spinner h="20px" w="20px" borderWidth="1px" color={colors.analyticGrey} />
              ) : (
                <>{isRtl ? <ChevronLeft /> : <ChevronRight />}</>
              )}
            </Flex>
          </Flex>
        </Flex>
      </WidgetWrapper>
    )
  }
)
